// File: webelieve-frontend/src/features/admin/components/settings/intake-forms/intakeFormApi.ts
// EF-6: Frontend API client for intake form management

import { apiClient } from '@/config/api';

// --- Types ---

export type FormFieldType =
  | 'text' | 'email' | 'phone' | 'textarea' | 'select' | 'multiselect'
  | 'checkbox' | 'date' | 'file' | 'number' | 'address' | 'currency';

export interface FormFieldOption {
  value: string;
  label: string;
}

export interface FormFieldValidation {
  min?: number;
  max?: number;
  pattern?: string;
  message?: string;
}

export interface FormFieldConditionalLogic {
  show_if_field: string;
  show_if_value: any;
}

export interface FormField {
  id: string;
  type: FormFieldType;
  label: string;
  placeholder?: string;
  required: boolean;
  validation?: FormFieldValidation;
  options?: FormFieldOption[];
  conditional_logic?: FormFieldConditionalLogic;
}

export interface FormSchema {
  title: string;
  description?: string;
  fields: FormField[];
  submit_button_text?: string;
  success_message?: string;
}

export interface FormSettings {
  practice_areas?: string[];
  auto_assign_to?: string;
  notification_emails?: string[];
  require_captcha?: boolean;
  allow_file_uploads?: boolean;
  max_file_size_mb?: number;
  redirect_url?: string;
  embed_css?: string;
}

export interface IntakeForm {
  form_id: string;
  realm_id: string;
  form_name: string;
  description?: string;
  form_schema: FormSchema;
  settings?: FormSettings;
  is_active: boolean;
  is_default: boolean;
  created_by_user_id: string;
  created_at: string;
  updated_at: string;
}

export interface CreateFormData {
  form_name: string;
  description?: string;
  form_schema: FormSchema;
  settings?: FormSettings;
  is_default?: boolean;
}

// --- API Client ---

const BASE = '/api/v1/admin/intake-forms';

export const intakeFormApi = {
  listForms: async (): Promise<IntakeForm[]> => {
    const res = await apiClient.get<{ data: IntakeForm[] }>(BASE);
    if (!res.success || !res.data) throw new Error(res.error || 'Failed to fetch forms');
    return (res.data as any);
  },

  getForm: async (formId: string): Promise<IntakeForm> => {
    const res = await apiClient.get<{ data: IntakeForm }>(`${BASE}/${formId}`);
    if (!res.success || !res.data) throw new Error(res.error || 'Failed to fetch form');
    return (res.data as any);
  },

  createForm: async (data: CreateFormData): Promise<IntakeForm> => {
    const res = await apiClient.post<{ data: IntakeForm }>(BASE, data);
    if (!res.success || !res.data) throw new Error(res.error || 'Failed to create form');
    return (res.data as any);
  },

  updateForm: async (formId: string, data: Partial<CreateFormData>): Promise<IntakeForm> => {
    const res = await apiClient.put<{ data: IntakeForm }>(`${BASE}/${formId}`, data);
    if (!res.success || !res.data) throw new Error(res.error || 'Failed to update form');
    return (res.data as any);
  },

  duplicateForm: async (formId: string, newName?: string): Promise<IntakeForm> => {
    const res = await apiClient.post<{ data: IntakeForm }>(`${BASE}/${formId}/duplicate`, { new_name: newName });
    if (!res.success || !res.data) throw new Error(res.error || 'Failed to duplicate form');
    return (res.data as any);
  },

  deleteForm: async (formId: string): Promise<void> => {
    const res = await apiClient.delete(`${BASE}/${formId}`);
    if (!res.success) throw new Error(res.error || 'Failed to delete form');
  },
};
